/**
 * Staff utility functions for filtering and managing staff/employee data
 */

/**
 * Filters employees to show only active staff OR a specific employee by ID.
 * This is useful for dropdown lists where we want to show active staff members,
 * but also need to display an inactive staff member if they're currently selected.
 *
 * @param {Array} employees - Array of employee objects
 * @param {string} [includeId=""] - Optional ID of an employee to include even if not active
 * @returns {Array} Filtered array of employees
 */
export const filterActiveStaff = (employees, includeId = "") => {
  return (
    employees?.filter(
      (emp) =>
        emp.status === "Active" ||
        emp.status === undefined ||
        emp.id === includeId,
    ) || []
  );
};

/**
 * Single-employee predicate: active + bookable calendar. Used directly on
 * surfaces (CalendarContext, CalendarTab, StaffSelectionStep) that already
 * have their own filter chain and don't need the includeId escape hatch.
 *
 * Keeping this beside filterBookableActiveStaff so the "what counts as
 * bookable + active" rule lives in exactly one place — if it ever changes
 * (new status, extra flag), the dropdown helper and the inline filters
 * stay in lockstep.
 *
 * @param {object} emp - Employee object
 * @returns {boolean}
 */
export const isBookableActiveEmployee = (emp) =>
  (emp.status === "Active" || emp.status === undefined) &&
  emp.has_bookable_calendar !== false;

/**
 * Filters employees down to those eligible to be BOOKED — active status AND
 * a bookable calendar (has_bookable_calendar !== false). Use this for any
 * dropdown that picks the staff who will perform work on a new appointment
 * (admin appointment create, rebook flow, add-service-to-existing-appt).
 *
 * Includes a specific employee even when they no longer qualify, so an edit
 * form can keep showing the existing assignee.
 *
 * For surfaces that legitimately list ALL staff (commission settings,
 * payroll, tips, reports), keep using filterActiveStaff — hidden-calendar
 * staff still earn commissions and need to show up on those screens.
 *
 * @param {Array} employees - Array of employee objects
 * @param {string} [includeId=""] - Optional ID to include even if not bookable
 * @returns {Array} Filtered array of employees
 */
export const filterBookableActiveStaff = (employees, includeId = "") => {
  return (
    employees?.filter(
      (emp) => emp.id === includeId || isBookableActiveEmployee(emp),
    ) || []
  );
};
